import type { FC } from 'react'
import { Container, Divider, Link, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import {
  backIconSx,
  backLinkSx,
  containerSx,
  dividerSx,
  inlineLinkSx,
  listStyle,
  sectionSx,
  sectionTitleSx,
  titleSx,
} from '../styles'

export const OnboardingPage: FC = () => {
  return (
    <Container maxWidth="md" sx={containerSx}>
      <Link component={RouterLink} to="/docs" sx={backLinkSx}>
        <ArrowBackIcon sx={backIconSx} />
        Back to Docs
      </Link>

      <Typography variant="h5" sx={titleSx}>
        Onboarding
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={sectionSx}>
        The onboarding flow runs for <strong>new users</strong> signing in for the first time. The
        dashboard walks through three setup steps — the first is required, the other two can be
        skipped and completed later from the settings page.
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={sectionSx}>
        <strong>Returning users</strong> who have already onboarded are prompted for the key ring on
        sign-in unless it is stored in localStorage (configurable in settings, not recommended for
        ultimate security).
      </Typography>

      <Divider sx={dividerSx} />

      <Typography variant="h6" sx={sectionTitleSx}>
        1. Encryption Key Ring
      </Typography>
      <Typography variant="body1" color="text.secondary" component="div" sx={sectionSx}>
        <ul style={listStyle}>
          <li>Required before anything else — unlocks the remaining steps.</li>
          <li>
            Generates an AES-256 key ring used for{' '}
            <Link component={RouterLink} to="/docs/security" sx={inlineLinkSx}>
              end-to-end encryption
            </Link>{' '}
            between the dashboard and the{' '}
            <Link component={RouterLink} to="/docs/daemon-setup" sx={inlineLinkSx}>
              daemon
            </Link>
            .
          </li>
          <li>
            A JSON backup file is automatically downloaded. Keep it safe — treat it like a password.
          </li>
          <li>
            If you lose the key ring: recreate and import from the settings page. If the backup is
            also lost, generate a new key ring — but previously encrypted dashboard data becomes
            unreadable.
          </li>
        </ul>
      </Typography>

      <Divider sx={dividerSx} />

      <Typography variant="h6" sx={sectionTitleSx}>
        2. API Key
      </Typography>
      <Typography variant="body1" color="text.secondary" component="div" sx={sectionSx}>
        <ul style={listStyle}>
          <li>Authenticates the daemon with the backend.</li>
          <li>Configure a name, environment (Development / Test / Production), and expiration.</li>
          <li>
            The key is shown once after generation — copy it immediately. It cannot be retrieved
            later.
          </li>
          <li>
            The key prefix determines the backend environment: <code>c4-live-*</code> routes to
            production, <code>c4-test-*</code> and <code>c4-dev-*</code> allow local backend
            configuration.
          </li>
          <li>Additional keys can be generated from the settings page.</li>
        </ul>
      </Typography>

      <Divider sx={dividerSx} />

      <Typography variant="h6" sx={sectionTitleSx}>
        3. Host Registration
      </Typography>
      <Typography variant="body1" color="text.secondary" component="div" sx={sectionSx}>
        <ul style={listStyle}>
          <li>Registers a machine that will run the daemon.</li>
          <li>
            Provide a descriptive name for the machine. The backend generates a unique host ID.
          </li>
          <li>
            Copy both the host name and host ID — you will need them during{' '}
            <Link component={RouterLink} to="/docs/daemon-setup" sx={inlineLinkSx}>
              daemon setup
            </Link>
            .
          </li>
          <li>
            Register one host per machine. Additional hosts can be registered from the settings
            page.
          </li>
        </ul>
      </Typography>

      <Divider sx={dividerSx} />

      <Typography variant="h6" sx={sectionTitleSx}>
        After Onboarding
      </Typography>
      <Typography variant="body1" color="text.secondary" component="div" sx={sectionSx}>
        <ul style={listStyle}>
          <li>
            All three artifacts (key ring file, API key, host ID) are needed to{' '}
            <Link component={RouterLink} to="/docs/daemon-setup" sx={inlineLinkSx}>
              set up the daemon
            </Link>
            .
          </li>
          <li>
            Key ring backup, API keys, and hosts can be managed from the settings page at any time.
          </li>
        </ul>
      </Typography>
    </Container>
  )
}
